// One-tap A4 print view for a pump daily report.
// Renders a print-formatted HTML document into a hidden iframe and calls
// print() — avoids popup blockers (no window.open).

const num = (v) => Number(v || 0);
const inr = (v) => `Rs ${num(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const qty = (v) => num(v).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const row = (cells, bold = false) =>
  `<tr${bold ? ' class="b"' : ''}>${cells.map((c, i) => `<td${i > 0 ? ' class="r"' : ''}>${c}</td>`).join('')}</tr>`;

// Shared A4 print stylesheet used by both the day report and party statement.
const BASE_CSS = `
    @page { size: A4; margin: 14mm 12mm; }
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; color: #111; font-size: 11px; margin: 0; }
    .head { text-align: center; border-bottom: 2px solid #0e7490; padding-bottom: 8px; margin-bottom: 10px; }
    .head h1 { margin: 0; font-size: 18px; color: #0e7490; letter-spacing: 1px; }
    .head p { margin: 2px 0 0; font-size: 11px; color: #444; }
    h3 { font-size: 12px; margin: 12px 0 4px; padding: 3px 6px; background: #eef6f9; border-left: 3px solid #0e7490; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #bbb; padding: 3px 6px; text-align: left; }
    th { background: #f4f7f8; font-size: 10px; }
    td.r, th.r { text-align: right; }
    tr.b td { font-weight: bold; background: #fafafa; }`;

// ---- Day-report section builders (each returns table-row HTML) ----
const buildFuelRows = (r) => {
  const totals = [['MS', r.ms_totals || {}], ['HSD', r.hsd_totals || {}], ['CNG', r.cng_totals || {}], ['GANTRY', r.gantry_totals || {}]];
  return totals
    .filter(([, t]) => num(t.net_sale) || num(t.amount))
    .map(([label, t]) => row([label, qty(t.net_sale), t.rate ? inr(t.rate) : '-', inr(t.amount)]))
    .join('');
};

const buildBowserRow = (r) => {
  const bowser = r.bowser_total || {};
  return (num(bowser.sale) || num(bowser.amount)) ? row(['BOWSER', qty(bowser.sale), '-', inr(bowser.amount)]) : '';
};

const buildCreditRows = (r) => {
  const credits = (r.credit_sales || []).filter((c) => c.party_name);
  const srps = (r.srps_bills || []).filter((b) => b.party_name);
  const mfuel = (r.mfuel_bills || []).filter((b) => b.party_name);
  return [
    ...credits.map((c) => row([esc(c.party_name), 'RO CREDIT', esc(c.ledger_no || '-'), inr(c.amount)])),
    ...srps.map((b) => row([esc(b.party_name), 'SRPS', esc(b.bill_no || '-'), inr(b.amount)])),
    ...mfuel.map((b) => row([esc(b.party_name), 'M FUEL', esc(b.bill_no || '-'), inr(b.amount)])),
  ].join('');
};

const buildSettlementRows = (r) => (r.settlements || []).filter((s) => num(s.amount))
  .map((s) => row([esc(s.mode || s.name || '-'), '', '', inr(s.amount)])).join('');

const buildDenomRows = (r) => (r.cash_denominations || []).filter((d) => num(d.count))
  .map((d) => row([`${qty(d.note)} x ${qty(d.count)}`, '', '', inr(num(d.note) * num(d.count))])).join('');

const buildCompanyRows = (r) => (r.company_payments || []).filter((c) => c.company || num(c.amount))
  .map((c) => row([esc(c.company || '-'), esc(c.mode || ''), esc(c.ref || '-'), inr(c.amount)])).join('');

const fuelTotalAmount = (r) =>
  num((r.ms_totals || {}).amount) + num((r.hsd_totals || {}).amount) + num((r.cng_totals || {}).amount)
  + num((r.gantry_totals || {}).amount) + num((r.bowser_total || {}).amount);

const section = (title, head, body, totalLabel, totalVal) => (body ? `
    <h3>${title}</h3>
    <table>
      <thead><tr>${head.map((h, i) => `<th${i > 0 ? ' class="r"' : ''}>${h}</th>`).join('')}</tr></thead>
      <tbody>${body}${totalLabel ? row([totalLabel, '', '', inr(totalVal)], true) : ''}</tbody>
    </table>` : '');

const buildDaySummaryTable = (r, totalSale, settleTotal) => {
  const cs = r.cash_summary || {};
  return `
    <h3>Day Summary</h3>
    <table><tbody>
      ${row(['Lube Sale', '', '', inr(r.lube_total_amount)])}
      ${row(['Total Sale', '', '', inr(totalSale)], true)}
      ${row(['Credit Sale', '', '', inr(r.total_credit_sale)])}
      ${row(['Settlements (POS/UPI)', '', '', inr(settleTotal)])}
      ${row(['Cash Sale', '', '', inr(cs.net_amount)])}
      ${row(['Total Cash Deposit', '', '', inr(cs.total_cash_deposit || cs.deposit_in_bank)], true)}
      ${num(r.loss_gain) ? row(['Loss / Gain', '', '', inr(r.loss_gain)]) : ''}
    </tbody></table>`;
};

export const buildDayReportHtml = (r) => {
  const ds = r.day_summary || {};
  const fuelRows = buildFuelRows(r) + buildBowserRow(r);
  const creditRows = buildCreditRows(r);
  const settleRows = buildSettlementRows(r);
  const settleTotal = (r.settlements || []).reduce((a, s) => a + num(s.amount), 0);
  const totalSale = ds.total_amount || (fuelTotalAmount(r) + num(r.lube_total_amount));

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Pump Day Report ${esc(r.date)}</title>
  <style>${BASE_CSS}
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0 16px; }
    .sig { margin-top: 26px; display: flex; justify-content: space-between; font-size: 11px; }
    .sig span { border-top: 1px solid #333; padding-top: 4px; width: 160px; text-align: center; }
  </style></head><body>
    <div class="head">
      <h1>SRPS FILLING STATION</h1>
      <p>Daily Sales Report — <b>${esc(r.date)}</b>${r.operator_name ? ` · Operator: ${esc(r.operator_name)}` : ''}</p>
    </div>

    ${section('Fuel Sales', ['Product', 'Qty (L/Kg)', 'Rate', 'Amount'], fuelRows, 'TOTAL FUEL', fuelTotalAmount(r))}

    ${buildDaySummaryTable(r, totalSale, settleTotal)}

    <div class="grid">
      <div>${section('Settlements', ['Mode', '', '', 'Amount'], settleRows, settleRows ? 'TOTAL' : '', settleTotal)}</div>
      <div>${section('Cash Denominations', ['Notes', '', '', 'Amount'], buildDenomRows(r), '', 0)}</div>
    </div>

    ${section('Credit Sales (Party-wise)', ['Party', 'Channel', 'Ref', 'Amount'], creditRows, creditRows ? 'TOTAL CREDIT' : '', num(r.total_credit_sale))}
    ${section('Payment Received by Companies', ['Company', 'Mode', 'Ref', 'Amount'], buildCompanyRows(r), '', 0)}

    <div class="sig"><span>Operator Signature</span><span>Manager Signature</span></div>
  </body></html>`;
};

export const printDayReport = (report) => {
  printHtml(buildDayReportHtml(report));
};

// Printable A4 party statement: credit entries + payments received + totals.
export const buildPartyStatementHtml = (ledger, range = {}) => {
  const period = range.from || range.to
    ? `Period: ${esc(range.from || 'Beginning')} to ${esc(range.to || 'Today')}`
    : 'Period: All time';
  const entryRows = (ledger.entries || [])
    .map((e) => row([esc(e.date), esc(e.channel || '-'), esc(e.ref || '-'), e.qty ? qty(e.qty) : '-', inr(e.amount)]))
    .join('');
  const payRows = (ledger.payments || [])
    .map((p) => row([esc(p.date), esc(p.method || '-'), esc(p.note || '-'), '', inr(p.amount)]))
    .join('');
  const outstanding = num(ledger.outstanding ?? ledger.total_amount);
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Statement — ${esc(ledger.party)}</title>
  <style>${BASE_CSS}
    .totals { margin-top: 12px; width: 60%; margin-left: auto; }
    .totals td { padding: 4px 8px; }
    .out td { font-weight: bold; font-size: 13px; color: ${outstanding > 0 ? '#b91c1c' : '#047857'}; }
    .sig { margin-top: 30px; display: flex; justify-content: space-between; font-size: 11px; }
    .sig span { border-top: 1px solid #333; padding-top: 4px; width: 180px; text-align: center; }
  </style></head><body>
    <div class="head">
      <h1>SRPS FILLING STATION</h1>
      <p>Party Statement — <b>${esc(ledger.party)}</b></p>
      <p>${period} · Generated: ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
    </div>

    <h3>Credit Purchases (${(ledger.entries || []).length} entries · ${qty(ledger.total_qty)} L)</h3>
    <table>
      <thead><tr><th>Date</th><th>Channel</th><th>Ref</th><th>Qty (L)</th><th>Amount</th></tr></thead>
      <tbody>${entryRows || row(['No credit entries in this period', '', '', '', '-'])}
      ${entryRows ? row(['TOTAL CREDIT', '', '', qty(ledger.total_qty), inr(ledger.total_amount)], true) : ''}</tbody>
    </table>

    <h3>Payments Received (${(ledger.payments || []).length})</h3>
    <table>
      <thead><tr><th>Date</th><th>Method</th><th>Note</th><th></th><th>Amount</th></tr></thead>
      <tbody>${payRows || row(['No payments in this period', '', '', '', '-'])}
      ${payRows ? row(['TOTAL PAID', '', '', '', inr(ledger.total_paid)], true) : ''}</tbody>
    </table>

    <table class="totals">
      <tbody>
        <tr><td>Total Credit</td><td style="text-align:right">${inr(ledger.total_amount)}</td></tr>
        <tr><td>Total Paid</td><td style="text-align:right">${inr(ledger.total_paid || 0)}</td></tr>
        <tr class="out"><td>OUTSTANDING BALANCE</td><td style="text-align:right">${inr(outstanding)}</td></tr>
      </tbody>
    </table>

    <div class="sig"><span>Party Signature</span><span>Authorised Signatory</span></div>
  </body></html>`;
};

export const printPartyStatement = (ledger, range) => {
  printHtml(buildPartyStatementHtml(ledger, range));
};

export const printHtml = (html) => {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  iframe.onload = () => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => iframe.remove(), 60000);
  };
};
